/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One cooking group, on Today.
 *
 * ── WHY ONE GROUP AND NOT ALL OF THEM ───────────────────────────────────────
 * The first version showed the newest posts across every group. That is the
 * right default and the wrong steady state: a household in four groups gets a
 * pane whose character changes depending on who happened to post last, and the
 * group they actually care about — the family, the supper club — competes with
 * the others for four slots.
 *
 * Pinning one makes this a PLACE rather than a feed. You learn where to look,
 * and what you see there is the same conversation each time.
 *
 * ── AND WHY IT STILL WORKS WITHOUT A PIN ────────────────────────────────────
 * Until someone chooses, the server falls back to whichever group is most
 * recently active — so the pane is useful before anyone has visited Settings,
 * which is most people. The card says which of the two it is doing, because
 * "your family" and "whichever group posted last" are different promises and a
 * pane that silently changed group would teach people not to trust it.
 *
 * ── WHAT IT SHOWS ───────────────────────────────────────────────────────────
 * The last few messages, photo-first. Not a count of unread — "3 unread" is an
 * obligation, a thumbnail of someone's pie is an invitation, and only the
 * second one gets people into a cooking group.
 */

import { Link } from "react-router-dom";

import type { CircleActivity } from "../../api/types.ts";
import { Icon } from "../../components/Icon.tsx";
import { CirclePhoto } from "../circles/CirclePhoto.tsx";

function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function tone(label: string): string {
  const tones = [
    "bg-accent/15 text-accent", "bg-teal/15 text-teal", "bg-navy/15 text-navy",
    "bg-ok/15 text-ok", "bg-warn/20 text-ink-2",
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  return tones[Math.abs(hash) % tones.length]!;
}

export function GroupActivityCard({ activity }: { activity: CircleActivity | undefined }) {
  // A server that doesn't send the field yet, or a household that turned the
  // feature off in Settings. Both mean "render nothing" — an opt-out must
  // actually remove the thing from view.
  if (!activity?.enabled) return null;

  const pinned = activity.pinned;

  // ── in no groups yet ──────────────────────────────────────────────────────
  // One quiet card, not a promotional panel. It explains the feature once, in
  // the place someone will actually see it, and disappears permanently the
  // moment they join anything.
  if (!pinned) {
    return (
      <section className="card p-4">
        <h2 className="label-mono mb-2">Cooking groups</h2>
        <p className="text-sm text-ink-2">
          Share what you're cooking with friends and family — photos, how it went,
          and recipes worth passing on. Private, invite-only.
        </p>
        <Link to="/circles" className="btn-ghost mt-3 w-full text-xs">
          <Icon name="chat" className="h-4 w-4" />
          Start or join a group
        </Link>
      </section>
    );
  }

  return (
    <section className="card p-4" aria-label={`Recent posts in ${pinned.name}`}>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h2 className="label-mono truncate">{pinned.name}</h2>
        <Link to={`/circles/${pinned.id}`} className="shrink-0 text-xs text-accent hover:underline">
          {pinned.unreadCount > 0 ? `${pinned.unreadCount} new` : "Open"}
        </Link>
      </div>

      {/* Says which group this is and why, when the choice wasn't made by a
          person. Without it, a pane that changes group after a quiet week looks
          like a bug. */}
      {!pinned.isExplicit && activity.groupCount > 1 && (
        <p className="type-meta mb-3">
          Your most active group ·{" "}
          <Link to="/settings" className="text-accent hover:underline">choose one</Link>
        </p>
      )}

      {pinned.messages.length === 0 ? (
        <p className="mt-2 text-sm text-ink-3">
          Nothing posted yet. Put up a photo of tonight's dinner — it's usually
          what gets a group going.
        </p>
      ) : (
        <ul className={`space-y-3 ${pinned.isExplicit ? "mt-3" : ""}`}>
          {pinned.messages.map((item) => (
            <li key={item.messageId}>
              <Link
                to={`/circles/${pinned.id}`}
                className="group flex gap-3 rounded-lg p-1 -m-1 transition hover:bg-paper-3"
              >
                {/* The photo, when there is one. It is the reason this card
                    works — a thumbnail of a real dinner is an invitation in a
                    way that an unread count never is. */}
                {item.photoId ? (
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                    <CirclePhoto
                      circleId={item.circleId}
                      messageId={item.messageId}
                      photoId={item.photoId}
                      aspect="square"
                    />
                  </div>
                ) : (
                  <span aria-hidden className={`grid h-14 w-14 shrink-0 place-items-center rounded-lg text-sm font-semibold ${
                    tone(item.authorLabel)}`}>
                    {item.authorLabel.trim().charAt(0).toUpperCase() || "?"}
                  </span>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="truncate text-sm font-medium text-ink">
                      {item.isOwnHousehold ? "You" : item.authorLabel}
                    </span>
                    <time className="shrink-0 text-[11px] text-ink-3" dateTime={item.createdAt}>
                      {ago(item.createdAt)}
                    </time>
                  </div>
                  {item.suggestedTitle && (
                    <div className="truncate text-xs font-medium text-accent">
                      {item.suggestedTitle}
                    </div>
                  )}
                  {item.body && (
                    <p className="line-clamp-2 text-xs leading-snug text-ink-3">{item.body}</p>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
